import axios from 'axios'

/**
 * 根据图片接口地址请求blob，大于2M自动压缩至2M内，返回base64
 * @param {String} imgUrl 图片后端接口地址
 * @param {Number} maxByte 最大限制字节 默认2M
 * @returns {Promise<String>} base64字符串
 */
export async function getImgBase64(imgUrl, token, maxByte = 2 * 1024 * 1024) {
  // 1. 请求blob，必须配置responseType
  const res = await axios.get(imgUrl, {
    responseType: 'blob',
    'Authorization': token
  })
  let blob = res.data

  // 拦截后端异常：返回json报错的情况
  if (blob.type.includes('application/json')) {
    const text = await blob.text()
    const err = JSON.parse(text)
    throw new Error(err.msg || '图片获取失败')
  }

  // 2. 超过2M执行压缩
  if (blob.size > maxByte) {
    blob = await compressImageBlob(blob, maxByte)
  }

  // 3. blob转base64返回
  return blob2Base64(blob)
}

/**
 * blob转base64
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
function blob2Base64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(blob)
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
  })
}

/**
 * 压缩图片blob，循环降低画质直到小于maxByte，原生处理手机图片旋转，不依赖exif-js
 * @param {Blob} blob 原图blob
 * @param {Number} maxByte 最大字节限制
 * @returns {Promise<Blob>} 压缩后blob
 */
function compressImageBlob(blob, maxByte) {
  return new Promise(async (resolve, reject) => {
    // 原生读取图片旋转角度，解决手机拍照倒置
    const orientation = await getImgOrientation(blob)
    const blobUrl = URL.createObjectURL(blob)
    const img = new Image()

    img.onload = () => {
      URL.revokeObjectURL(blobUrl)
      // 限制图片最大宽度1080，移动端适配
      const limitWidth = 1080
      let w = img.width
      let h = img.height
      if (w > limitWidth) {
        h = Math.round((h * limitWidth) / w)
        w = limitWidth
      }

      // 创建画布，根据旋转标识交换宽高
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if ([5, 6, 7, 8].includes(orientation)) {
        canvas.width = h
        canvas.height = w
      } else {
        canvas.width = w
        canvas.height = h
      }

      // 画布旋转变换，修复旋转
      switch (orientation) {
        case 2: ctx.transform(-1, 0, 0, 1, w, 0); break
        case 3: ctx.transform(-1, 0, 0, -1, w, h); break
        case 4: ctx.transform(1, 0, 0, -1, 0, h); break
        case 5: ctx.transform(0, 1, 1, 0, 0, 0); break
        case 6: ctx.transform(0, 1, -1, 0, 0, w); break
        case 7: ctx.transform(0, -1, -1, 0, h, w); break
        case 8: ctx.transform(0, -1, 1, 0, h, 0); break
      }

      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.drawImage(img, 0, 0, w, h)

      // 迭代压缩：画质从0.8降到最低0.2
      const loopCompress = (quality) => {
        canvas.toBlob(
          (resBlob) => {
            if (resBlob.size <= maxByte || quality <= 0.2) {
              resolve(resBlob)
            } else {
              loopCompress(quality - 0.1)
            }
          },
          'image/jpeg',
          quality
        )
      }
      loopCompress(0.8)
    }

    img.onerror = () => {
      URL.revokeObjectURL(blobUrl)
      reject(new Error('图片加载失败'))
    }
    img.src = blobUrl
  })
}

/**
 * 纯原生读取EXIF旋转标识，无需exif-js
 * @param {Blob} blob
 * @returns {Promise<number>} 旋转标识 1~8
 */
function getImgOrientation(blob) {
  return new Promise((resolve) => {
    // 只读取图片头部64KB，性能更高
    const reader = new FileReader()
    reader.readAsArrayBuffer(blob.slice(0, 65536))
    reader.onload = function (e) {
      const view = new DataView(e.target.result)
      let offset = 0
      // 判断jpg头标识
      if (view.getUint16(offset, false) !== 0xFFD8) {
        resolve(1)
        return
      }
      offset += 2
      while (offset < view.byteLength) {
        const marker = view.getUint16(offset, false)
        offset += 2
        if (marker === 0xFFE1) {
          const exifLen = view.getUint16(offset, false)
          // 判断exif标识
          if (view.getUint32(offset + 2, false) === 0x45786966) {
            const littleEndian = view.getUint16(offset + 6, false) === 0x4949
            const tagStart = offset + 8
            const tagCount = view.getUint16(tagStart, littleEndian)
            let tagPos = tagStart + 2
            for (let i = 0; i < tagCount; i++) {
              const tag = view.getUint16(tagPos, littleEndian)
              // Orientation标签
              if (tag === 0x0112) {
                const val = view.getUint16(tagPos + 8, littleEndian)
                resolve(val)
                return
              }
              tagPos += 12
            }
          }
          offset += exifLen
        } else {
          offset += view.getUint16(offset, false)
        }
      }
      // 无旋转信息默认1
      resolve(1)
    }
    reader.onerror = () => resolve(1)
  })
}



// WEBPACK FOOTER //
// ./src/utils/imgBlobToBase64.js